import React, { useState, useEffect } from "react";
import { usePlatformThemeMode, tc, themeColors } from "./themeUtils";
import { anchorDerivedColors } from "../utils/color";
import { buildDarkAccentCss, buildLightAccentCss } from "../utils/themeAccent";
import CommandNav from "./CommandNav";

export default function LegalPage({platform, pageKey, lang, setLang, onBack}){
  const isRTL=lang==="ar";
  /* Accent default aligned with the landing (violet primary from the
     iridescent gradient system). Admin override via platform.platformAccent
     still wins. */
  const accent=platform.platformAccent||"#7c3aed";
  const theme=usePlatformThemeMode(platform);
  const C=themeColors(theme);
  const legal=platform.legalPages||{};
  const TITLES_EN={privacyPolicy:"Privacy Policy",termsOfService:"Terms of Service",userDataDeletion:"Data Deletion Policy",supportPage:"Support"};
  const TITLES_AR={privacyPolicy:"سياسة الخصوصية",termsOfService:"شروط الخدمة",userDataDeletion:"سياسة حذف البيانات",supportPage:"الدعم"};
  const title=isRTL?(TITLES_AR[pageKey]||"قانوني"):(TITLES_EN[pageKey]||"Legal");
  const content=isRTL?(legal[pageKey+"Ar"]||legal[pageKey]||""):(legal[pageKey]||"");
  const parts=(platform.platformName||"Command").split(" ");
  const accentCss=theme==="dark"?buildDarkAccentCss(accent):buildLightAccentCss(accent);

  /* ── Mobile detection + palette ── */
  const [isMobileLegal,setIsMobileLegal]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=768);
  useEffect(()=>{const h=()=>setIsMobileLegal(window.innerWidth<=768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  const lgAcc=platform.wsDefaultAnchorColor||accent;
  const {primary:lgP,palette:lgPal}=anchorDerivedColors(lgAcc);
  const lgInk=theme==="dark"?lgPal[10]:lgP;
  const lgMuted=theme==="dark"?lgPal[6]:lgPal[5];
  const lgSubtle=theme==="dark"?lgPal[4]:lgPal[7];
  const lgBody=theme==="dark"?lgPal[8]:lgPal[4];
  const lgAccentCss=theme==="dark"?buildDarkAccentCss(lgAcc):buildLightAccentCss(lgAcc);
  const lgFont=isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,'SF Pro Display','SF Pro Text','Helvetica Neue',sans-serif";

  /* ══ MOBILE LEGAL PAGE ══ */
  if(isMobileLegal){
    return(
      <div className={`${theme==="dark"?"p-dark":"p-light"}${isRTL?" rtl":""} pml-wrap`}>
        <style>{`\nbody{background:${theme==="dark"?"#08080f":"#FAFAF9"};overflow:hidden;margin:0;}`}</style>
        {lgAccentCss&&<style>{lgAccentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        <div className="p-hero-grid" style={{position:"fixed"}}/>
        <div className="pml-slide" style={{flex:1,minWidth:"auto",width:"auto"}}>
          <div className="pml-glass" style={{maxHeight:"85vh",overflowY:"auto",display:"flex",flexDirection:"column"}}>
            {/* Header */}
            <div style={{flexShrink:0,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <h1 style={{fontFamily:lgFont,fontSize:22,fontWeight:700,color:lgInk,margin:0}}>{title}</h1>
                <button onClick={()=>setLang(lang==="en"?"ar":"en")}
                  style={{display:"flex",alignItems:"center",justifyContent:"center",background:theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)",
                    border:`1px solid ${theme==="dark"?"rgba(255,255,255,.1)":"rgba(0,0,0,.06)"}`,
                    borderRadius:20,width:30,height:30,padding:0,cursor:"pointer",flexShrink:0}}>
                  <span style={{fontSize:16,lineHeight:1}}>{lang==="en"?"\ud83c\uddf8\ud83c\udde6":"\ud83c\uddec\ud83c\udde7"}</span>
                </button>
              </div>
              <div style={{fontSize:11,color:lgMuted}}>
                {legal.companyName||"Command Platform"}{legal.companyAddress?" · "+legal.companyAddress:""}
                {legal.contactEmail&&<span> · <a href={`mailto:${legal.contactEmail}`} style={{color:lgAcc}}>{legal.contactEmail}</a></span>}
              </div>
            </div>
            {/* Content */}
            <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",minHeight:0}}>
              {content?(
                <div dir={isRTL?"rtl":"ltr"} style={{fontSize:14,lineHeight:1.8,color:lgBody}} dangerouslySetInnerHTML={{__html:content}}/>
              ):(
                <div style={{textAlign:"center",padding:"32px 0"}}>
                  <div style={{fontSize:15,color:lgMuted,marginBottom:6}}>{isRTL?"المحتوى غير متوفر بعد":"Content not yet available"}</div>
                  <div style={{fontSize:12,color:lgSubtle}}>{isRTL?`لم يتم نشر ${title} بعد. تواصل مع المسؤول.`:`The ${title.toLowerCase()} has not been published yet.`}</div>
                </div>
              )}
            </div>
            {/* Footer + back */}
            <div style={{flexShrink:0,paddingTop:16,borderTop:`1px solid ${theme==="dark"?"rgba(255,255,255,.06)":"rgba(0,0,0,.04)"}`,marginTop:12}}>
              <div style={{fontSize:10,color:lgSubtle,textAlign:"center",marginBottom:10}}>© {new Date().getFullYear()} {legal.companyName||"Command Platform"}</div>
              <button onClick={onBack} style={{background:"none",border:"none",color:lgMuted,fontSize:13,cursor:"pointer",padding:"8px 0",width:"100%",textAlign:"center"}}>{isRTL?"رجوع":"Back"}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ══ DESKTOP LEGAL PAGE ══ */
  return(
    <div className={`cmd-landing ${theme==="dark"?"p-dark":"p-light"}`} style={{minHeight:"100vh",fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif",display:"flex",flexDirection:"column"}}>

      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>
      <CommandNav
        logoSrc={platform.platformLogo||"/icon.svg"}
        logoAlt={platform.platformName||"Command"}
        lang={lang}
        setLang={setLang}
        onLogoClick={onBack}
        onBack={onBack}
        backText={isRTL?"رجوع":"Back"}
        showNavLinks={false}
        showSignIn={false}
        showSignUp={false}
      />
      <div style={{flex:1,display:"flex",justifyContent:"center",padding:"100px 24px 40px",position:"relative",zIndex:1}}>
        <div style={{maxWidth:860,width:"100%"}}>
          <h1 style={{fontSize:28,fontWeight:700,color:C.text,marginBottom:8}}>{title}</h1>
          <div style={{fontSize:11,color:C.muted,marginBottom:24}}>
            {legal.companyName||"Command Platform"}{legal.companyAddress?" · "+legal.companyAddress:""}
            {legal.contactEmail&&<span> · <a href={`mailto:${legal.contactEmail}`} style={{color:accent}}>{legal.contactEmail}</a></span>}
          </div>
          {content?(
            <div dir={isRTL?"rtl":"ltr"} style={{background:C.contentBg,borderRadius:12,border:"1px solid "+C.contentBd,padding:"28px 32px",color:C.contentText,fontSize:14,lineHeight:1.8,boxShadow:theme==="light"?"0 1px 3px rgba(0,0,0,.04)":"none"}} dangerouslySetInnerHTML={{__html:content}}/>
          ):(
            <div style={{background:C.contentBg,borderRadius:12,border:"1px solid "+C.contentBd,padding:"40px 32px",textAlign:"center"}}>
              <div style={{fontSize:16,color:C.muted,marginBottom:8}}>{isRTL?"المحتوى غير متوفر بعد":"Content not yet available"}</div>
              <div style={{fontSize:13,color:C.subtle}}>{isRTL
                ?`لم يتم نشر ${title} الخاصة بـ ${platform.platformName||"Command"} بعد. يرجى التواصل مع ${legal.contactEmail||"المسؤول"} للمزيد من المعلومات.`
                :`The ${title.toLowerCase()} for ${platform.platformName||"Command"} has not been published yet. Please contact ${legal.contactEmail||"the administrator"} for more information.`}</div>
            </div>
          )}
        </div>
      </div>
      <footer style={{padding:"20px 24px",textAlign:"center",fontSize:11,color:C.footerText,position:"relative",zIndex:1}}>
        © {new Date().getFullYear()} {legal.companyName||"Command Platform"}. {isRTL?"جميع الحقوق محفوظة.":"All rights reserved."}
      </footer>
    </div>
  );
}
